/** Product catalogue endpoints (`/products`). Public list; management mutations. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getData, getList, patchData, postData } from "../lib/apiClient";
import { apiClient } from "../lib/apiClient";
import { ApiEnvelope, ListParams } from "../types/api";
import { queryKeys } from "./queryKeys";
import { Product, ProductType, UnitOfMeasure } from "../types/models";

export interface ListProductsParams extends ListParams {
  categoryId?: string;
  type?: ProductType;
  isActive?: boolean;
  minWeight?: number;
  maxWeight?: number;
  sortBy?: "name" | "price" | "stockQty" | "createdAt";
}

export interface CreateProductInput {
  name: string;
  sku?: string;
  categoryId: string;
  type?: ProductType;
  unit?: UnitOfMeasure;
  weightGrams?: number;
  packSize?: number;
  price?: number;
  costPrice?: number;
  lowStockThreshold?: number;
  description?: string;
  imageUrl?: string;
  openingStock?: number;
}

export interface UpdateProductInput {
  name?: string;
  categoryId?: string;
  type?: ProductType;
  unit?: UnitOfMeasure;
  weightGrams?: number | null;
  packSize?: number | null;
  price?: number;
  costPrice?: number;
  lowStockThreshold?: number;
  description?: string;
  isActive?: boolean;
}

export const productsApi = {
  list: (params?: ListProductsParams) => getList<Product>("/products", params),
  getById: (id: string) => getData<Product>(`/products/${id}`),
  create: (input: CreateProductInput) => postData<Product>("/products", input),
  update: (id: string, input: UpdateProductInput) =>
    patchData<Product>(`/products/${id}`, input),
  uploadImage: (id: string, file: File) => {
    const form = new FormData();
    form.append("image", file);
    return apiClient
      .post<ApiEnvelope<Product>>(`/products/${id}/image`, form)
      .then((r) => r.data.data);
  },
};

// ── Hooks ────────────────────────────────────────────────────────────

export const useProducts = (params?: ListProductsParams) =>
  useQuery({
    queryKey: queryKeys.products.list(params),
    queryFn: () => productsApi.list(params),
    placeholderData: (prev) => prev, // keep previous page visible while fetching
  });

export const useProduct = (id: string | undefined) =>
  useQuery({
    queryKey: queryKeys.products.detail(id ?? ""),
    queryFn: () => productsApi.getById(id as string),
    enabled: Boolean(id),
  });

export const useCreateProduct = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProductInput) => productsApi.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.products.all });
      qc.invalidateQueries({ queryKey: queryKeys.inventory.all });
    },
  });
};

export const useUpdateProduct = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateProductInput }) =>
      productsApi.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.products.all });
      qc.invalidateQueries({ queryKey: queryKeys.inventory.all });
    },
  });
};

export const useUploadProductImage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      productsApi.uploadImage(id, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.products.all }),
  });
};
